import {
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_LABELS,
  VISIT_STATUS_LABELS,
  formatDateShort,
  formatDateTime,
  formatDuration,
  formatMoney,
  type AuditEntryDto,
  type PaymentMethod,
  type PaymentStatus,
  type VisitStatus,
} from '@kominiarz/shared';
import styles from './visits.module.css';

const ACTION_LABELS: Record<string, string> = {
  create: 'Dodanie',
  update: 'Zmiana danych',
  close: 'Status lub płatność',
  delete: 'Usunięcie',
  add_object: 'Nowy adres',
  update_object: 'Zmiana adresu',
  remove_object: 'Usunięcie adresu',
  anonymize: 'Usunięcie danych osobowych',
  add_photos: 'Dodanie zdjęć',
  delete_photo: 'Usunięcie zdjęcia',
};

const FIELD_LABELS: Record<string, string> = {
  startAt: 'Termin',
  durationMinutes: 'Czas trwania',
  arrivalWindowMinutes: 'Przedział przyjazdu',
  client: 'Klient',
  address: 'Adres',
  phone: 'Telefon',
  serviceType: 'Usługi',
  description: 'Opis',
  amountGrosze: 'Kwota',
  assignee: 'Wykonawcy',
  visibility: 'Widoczność',
  sharedWith: 'Udostępniono',
  reminderMinutes: 'Przypomnienie',
  status: 'Status',
  afterNote: 'Notatka po wizycie',
  paymentStatus: 'Płatność',
  paymentMethod: 'Forma płatności',
  paidAt: 'Data opłacenia',
  name: 'Nazwa',
  email: 'E-mail',
  note: 'Notatka',
  phones: 'Telefony',
  label: 'Nazwa adresu',
  cancelledVisits: 'Odwołane wizyty',
  nextInspectionDue: 'Kolejny przegląd',
};

function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (field) {
    case 'startAt':
      return formatDateTime(String(value));
    case 'durationMinutes':
    case 'arrivalWindowMinutes':
      return formatDuration(Number(value));
    case 'amountGrosze':
      return formatMoney(Number(value));
    case 'visibility':
      return value === 'selected' ? 'wybrane osoby' : 'tylko autor i wykonawca';
    case 'sharedWith':
    case 'phones':
    case 'address':
      return Array.isArray(value) ? (value.length ? value.join('; ') : '—') : String(value);
    case 'reminderMinutes':
      return Number(value) === 0 ? 'bez przypomnienia' : formatDuration(Number(value));
    case 'status':
      return VISIT_STATUS_LABELS[value as VisitStatus] ?? String(value);
    case 'paymentStatus':
      return PAYMENT_STATUS_LABELS[value as PaymentStatus] ?? String(value);
    case 'paymentMethod':
      return PAYMENT_METHOD_LABELS[value as PaymentMethod] ?? String(value);
    case 'paidAt':
    case 'nextInspectionDue':
      return formatDateShort(String(value));
    default:
      return String(value);
  }
}

/** Manager-only change history: who changed what and when. */
export function AuditList({ entries }: { entries: AuditEntryDto[] }) {
  return (
    <ol className={styles.audit}>
      {entries.map((e) => (
        <li key={e.id} className={styles.auditEntry}>
          <p className={styles.auditHead}>
            <strong>{ACTION_LABELS[e.action] ?? e.action}</strong>
            <span className={styles.auditWhen}>
              {formatDateTime(e.createdAt)}, {e.user?.name ?? 'system'}
            </span>
          </p>
          {e.changes && !['create', 'delete', 'add_object', 'remove_object', 'add_photos', 'delete_photo'].includes(e.action) && (
            <ul className={styles.auditChanges}>
              {Object.entries(e.changes).map(([field, change]) => (
                <li key={field}>
                  {FIELD_LABELS[field] ?? field}: {formatValue(field, change.from)} → {formatValue(field, change.to)}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}
